import { normalizeKeyboardValue } from './normalizeKeyboardValue';

import type { DialPadValue } from 'features/dial-pad/interfaces';

// =====================================================
// ALT KEY DETECTION UTILITIES
// =====================================================

/**
 * Detects if Alt key is currently pressed or active.
 *
 * Handles multiple Alt key variations:
 * - Standard Alt key (event.altKey)
 * - AltGraph key (used on international keyboards)
 * - Alt modifier state (cross-platform compatibility)
 *
 * @param event - Keyboard event to check
 * @returns true if any Alt variant is active
 */
export const isAltKeyPressed = (event: KeyboardEvent): boolean => {
  return (
    event.altKey ||
    event.getModifierState?.('Alt') ||
    event.getModifierState?.('AltGraph')
  );
};

/**
 * Sets up real-time Alt key state tracking across the entire document.
 *
 * This solves the timing issue where:
 * 1. User presses number key (e.g., "2")
 * 2. User presses Alt key immediately after
 * 3. We need to detect Alt even after the number event fired
 *
 * @param altKeyRef - React ref that stores current Alt key state
 * @returns Cleanup function to remove event listeners
 */
export const createAltKeyTracker = (
  altKeyRef: React.MutableRefObject<boolean>
): (() => void) => {
  // When any Alt key is pressed down, mark it as active
  const onAltKeyDown = (event: KeyboardEvent) => {
    const isAnyAltPressed =
      event.key === 'Alt' ||
      event.altKey ||
      event.getModifierState?.('Alt') ||
      event.getModifierState?.('AltGraph');

    if (isAnyAltPressed) {
      altKeyRef.current = true;
    }
  };

  // When Alt key is released, mark it as inactive
  const onAltKeyUp = (event: KeyboardEvent) => {
    const isAltReleased =
      event.key === 'Alt' ||
      (!event.altKey &&
        !event.getModifierState?.('Alt') &&
        !event.getModifierState?.('AltGraph'));

    if (isAltReleased) {
      altKeyRef.current = false;
    }
  };

  // Listen to all keyboard events (capture phase = early detection)
  document.addEventListener('keydown', onAltKeyDown, { capture: true });
  document.addEventListener('keyup', onAltKeyUp, { capture: true });

  // Return cleanup function for when component unmounts
  return () => {
    document.removeEventListener('keydown', onAltKeyDown, { capture: true });
    document.removeEventListener('keyup', onAltKeyUp, { capture: true });
  };
};

// =====================================================
// DIAL PAD INPUT VALIDATION
// =====================================================

/**
 * Determines if a keyboard input should be blocked from reaching the dial pad.
 *
 * Blocks input when:
 * - Alt key is currently pressed (prevents accidental shortcuts)
 * - Dial pad is closed (no point processing input)
 * - Key is held down (prevents repeated input)
 * - Key is not allowed for dial pad (only numbers, *, # etc.)
 *
 * @param event - The keyboard event
 * @param isDialPadOpen - Whether dial pad is currently visible/active
 * @param isKeyAllowed - Function that checks if key is valid for dial pad
 * @returns true if input should be blocked, false if it should be processed
 */
export const shouldBlockDialPadInput = (
  event: KeyboardEvent,
  isDialPadOpen: boolean,
  isKeyAllowed: (key: string) => boolean
): boolean => {
  // Block if Alt is pressed (user might be trying keyboard shortcuts)
  if (isAltKeyPressed(event)) {
    return true;
  }

  // Block if basic conditions aren't met
  const hasBasicIssues =
    !isDialPadOpen || // Dial pad isn't open
    event.repeat || // Key is being held down
    !isKeyAllowed(event.key); // Key isn't valid for dial pad

  return hasBasicIssues;
};

/**
 * Checks if a key represents a number (0-9).
 *
 * @param key - The key string from keyboard event
 * @returns true if key is a single digit
 */
export const isNumberKey = (key: string): boolean => {
  return /^\d$/.test(key);
};

// =====================================================
// TIMING-BASED INPUT HANDLING
// =====================================================

/**
 * Handles numeric key input with a delay to catch Alt key presses.
 *
 * The Problem:
 * When user quickly presses "2" then "Alt", the "2" key event fires
 * before Alt is detected, causing unwanted dial tone.
 *
 * The Solution:
 * For numeric keys only, wait a short period before processing.
 * If Alt gets pressed during this delay, cancel the dial tone.
 *
 * @param event - Original keyboard event
 * @param altKeyRef - Real-time Alt key state tracker
 * @param isDialPadOpen - Whether dial pad is active
 * @param onDialPadInput - Function to call with the dial pad value
 * @param delayMs - How long to wait for Alt key (default: 100ms)
 */
export const handleNumericKeyWithDelay = (
  event: KeyboardEvent,
  altKeyRef: React.MutableRefObject<boolean>,
  isDialPadOpen: boolean,
  onDialPadInput: (value: DialPadValue) => void,
  delayMs = 50
): void => {
  setTimeout(() => {
    // After delay, check if Alt was pressed and dial pad is still open
    const shouldStillProcess = !altKeyRef.current && isDialPadOpen;

    if (shouldStillProcess) {
      const dialValue = normalizeKeyboardValue(event.key);
      onDialPadInput(dialValue);
    }
  }, delayMs);
};
